//== state container for the FHS redemption calculator
//== factory pattern: each createStore() call returns an isolated store
//== no module-level state; safe to instantiate multiple times in tests

import { isEarlyRedemption } from './early-redemption.js'
import { getValidSteps } from './validate.js'

export function createInitialState() {
  return {
    product: null,
    drawdownDate: null,
    buildComplete: null,
    isEarlyRedemption: false,
    validSteps: new Set([0]),
    inputs: {
      purchasePrice: null,
      buildCost: null,
      siteValue: null,
      originalEquity: null,
      currentEquityPct: null,
      currentValuation: null,
      serviceCharges: 0,
    },
    redemption: {
      type: null,
      amount: null,
    },
    currentStep: 0,
  }
}

//== createStore({ scheme, now })
//== - scheme: from window.calculatorData.scheme
//== - now:    optional Date for deterministic tests; production callers omit
export function createStore({ scheme, now } = {}) {
  let state = recompute(createInitialState(), { scheme, now })
  const subscribers = new Set()

  function notify() {
    for (const fn of subscribers) fn(state)
  }

  return {
    getState: () => state,

    setState(patch) {
      if (!patch || typeof patch !== 'object') return
      state = recompute(applyPatch(state, patch), { scheme, now })
      notify()
    },

    subscribe(fn) {
      subscribers.add(fn)
      return () => subscribers.delete(fn)
    },

    reset() {
      state = recompute(createInitialState(), { scheme, now })
      notify()
    },
  }
}

//== shallow-merges top-level keys; deep-merges the known nested objects
function applyPatch(prev, patch) {
  const next = { ...prev, ...patch }
  if (patch.inputs) next.inputs = { ...prev.inputs, ...patch.inputs }
  if (patch.redemption) next.redemption = { ...prev.redemption, ...patch.redemption }
  return next
}

//== recomputes derived fields after any patch
//== isEarlyRedemption first, then validSteps (which reads it)
function recompute(s, { scheme, now }) {
  const reference = now ?? new Date()
  const withEarly = {
    ...s,
    isEarlyRedemption: isEarlyRedemption({
      product: s.product,
      drawdownDate: s.drawdownDate,
      buildComplete: s.buildComplete,
      scheme,
      now: reference,
    }),
  }
  return {
    ...withEarly,
    validSteps: getValidSteps(withEarly, scheme, { now: reference }),
  }
}
